import React, { useMemo, useState } from "react"
import {
  faArrowTrendUp,
  faBullseye,
  faCalendar,
  faChartLine,
  faCoins,
  faFileInvoiceDollar,
  faSackDollar,
  faUserCheck,
} from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { AdminMetricCard } from "../components/AdminMetricCard"
import { SeriesChart } from "../components/SeriesChart"
import { AdminGrowthInvestmentDashboard } from "../../../Objects/AdminGrowthInvestment/AdminGrowthInvestmentDashboard"
import { AdminGrowthInvestmentFilter } from "../../../Objects/AdminGrowthInvestment/AdminGrowthInvestmentFilter"
import { AdminGrowthInvestment } from "../../../Objects/AdminGrowthInvestment/AdminGrowthInvestment"
import { convertToLocalMoney } from "../../../Objects/Generics/Helps"
import { AdminGrowthInvestmentPopup } from "../AdminGrowthInvestmentPopup"
import { useTranslation } from "react-i18next"
import InputField from "../../../components/InputField/InputField"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { EAdminGrowthInvestmentCategoryType } from "../../../Enum/EAdminGrowthInvestmentCategoryType"
import { ECommercialOriginType } from "../../../Enum/ECommercialOriginType"
import { getRestaurantNow } from "../../../utils/timeZone"
import List from "../../../components/List/List"
import { AdminListCard, AdminListMetricItem } from "../components/AdminListCard"

interface GrowthInvestmentsSectionProps {
  dashboard: AdminGrowthInvestmentDashboard
  filter: AdminGrowthInvestmentFilter
  onFilterChange: (filter: AdminGrowthInvestmentFilter) => void
  onRefresh: () => void
}

const dateOnly = (value?: string) => (value ? value.split("T")[0] : "")

const buildPresetFilter = (daysBack: number) => {
  const end = getRestaurantNow()
  const start = getRestaurantNow()

  start.setDate(end.getDate() - daysBack)

  return new AdminGrowthInvestmentFilter({
    startDate: start.toInputDate(),
    endDate: end.toInputDate(),
  })
}

const investmentCategoryTypes = [
  EAdminGrowthInvestmentCategoryType.OnlineAds,
  EAdminGrowthInvestmentCategoryType.PhysicalCampaign,
  EAdminGrowthInvestmentCategoryType.People,
  EAdminGrowthInvestmentCategoryType.InfluencerOrPartner,
  EAdminGrowthInvestmentCategoryType.Tooling,
  EAdminGrowthInvestmentCategoryType.Infrastructure,
  EAdminGrowthInvestmentCategoryType.Event,
  EAdminGrowthInvestmentCategoryType.Retention,
  EAdminGrowthInvestmentCategoryType.Other,
]

const GROWTH_INVESTMENT_FILTERS = [
  "growth_investment_filter_return",
  "growth_investment_filter_without_return",
  "growth_investment_filter_all",
] as const

export const GrowthInvestmentsSection: React.FC<GrowthInvestmentsSectionProps> = ({
  dashboard,
  filter,
  onFilterChange,
  onRefresh,
}) => {
  const { t } = useTranslation("admin")
  const [editing, setEditing] = useState<AdminGrowthInvestment | null>(null)

  const categoryOptions = useMemo(
    () => [
      { value: "", label: t("growth_investment_all_categories") },
      ...investmentCategoryTypes.map((value) => ({
        value: String(value),
        label: (value as number).toEAdminGrowthInvestmentCategoryTypeString(),
      })),
    ],
    [t],
  )

  const originOptions = useMemo(
    () => [
      { value: "", label: t("growth_investment_all_origins") },
      ...Object.values(ECommercialOriginType)
        .filter((value) => typeof value === "number")
        .map((value) => ({ value: String(value), label: (value as number).toECommercialOriginTypeString() })),
    ],
    [t],
  )

  const investmentSeries = useMemo(
    () => dashboard.monthlySeries.map((item) => ({ label: item.label, value: item.investmentAmount })),
    [dashboard.monthlySeries],
  )

  const returnSeries = useMemo(
    () =>
      dashboard.monthlySeries.map((item) => ({
        label: item.label,
        value: item.subscriptionRevenueAmount + item.associatedRevenueAmount,
      })),
    [dashboard.monthlySeries],
  )

  const resultSeries = useMemo(
    () => dashboard.monthlySeries.map((item) => ({ label: item.label, value: Math.abs(item.profitOrLossAmount) })),
    [dashboard.monthlySeries],
  )

  const topChannels = dashboard.channels.slice(0, 8).map((item) => ({ label: item.key, value: item.investmentAmount }))

  const topCampaigns = dashboard.campaigns
    .slice(0, 8)
    .map((item) => ({ label: item.key, value: item.associatedRevenueAmount }))
  const resultClass = dashboard.summary.profitOrLossAmount >= 0 ? "text-emerald-700" : "text-red-700"

  const dropdownFilters = GROWTH_INVESTMENT_FILTERS.map((key) => t(key))

  return (
    <div className="w-full overflow-auto bg-white h-100vh p-1 md:p-0">
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
        <div className="mx-1 mb-3 rounded-md border-2 border-orange-200 bg-white p-3 shadow-sm">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                {[
                  [t("growth_investment_daily"), 0],
                  [t("growth_investment_weekly"), 6],
                  [t("growth_investment_monthly"), 30],
                  [t("growth_investment_yearly"), 365],
                ].map(([label, days]) => (
                  <button
                    key={label}
                    className="min-h-10 rounded-md border-2 border-orange-200 bg-orange-50 px-3 py-2 text-xs font-bold text-orange-700 transition hover:bg-orange-100 sm:min-w-24"
                    onClick={() => onFilterChange(buildPresetFilter(days as number))}
                    data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.growthInvestmentsSection.button.onFilterChangeBuildPresetFilterDaysAsNumber"
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 xl:grid-cols-6">
              <InputField
                type="date"
                placeholder={t("growth_investment_start")}
                icon={faCalendar}
                value={filter.startDate ?? ""}
                onChange={(event) =>
                  onFilterChange(new AdminGrowthInvestmentFilter({ ...filter, startDate: event.target.value }))
                }
                descriptionKey="interactionDescriptions:pages.adminDashboard.sections.growthInvestmentsSection.input.growthInvestmentStart"
              />

              <InputField
                type="date"
                placeholder={t("growth_investment_end")}
                icon={faCalendar}
                value={filter.endDate ?? ""}
                onChange={(event) =>
                  onFilterChange(new AdminGrowthInvestmentFilter({ ...filter, endDate: event.target.value }))
                }
                descriptionKey="interactionDescriptions:pages.adminDashboard.sections.growthInvestmentsSection.input.growthInvestmentEnd"
              />

              <InputField
                type="text"
                placeholder={t("growth_investment_campaign")}
                icon={faChartLine}
                value={filter.campaignName ?? ""}
                onChange={(event) =>
                  onFilterChange(new AdminGrowthInvestmentFilter({ ...filter, campaignName: event.target.value }))
                }
                descriptionKey="interactionDescriptions:pages.adminDashboard.sections.growthInvestmentsSection.input.growthInvestmentCampaign"
              />

              <InputField
                type="text"
                placeholder={t("growth_investment_channel")}
                icon={faBullseye}
                value={filter.channelName ?? ""}
                onChange={(event) =>
                  onFilterChange(new AdminGrowthInvestmentFilter({ ...filter, channelName: event.target.value }))
                }
                descriptionKey="interactionDescriptions:pages.adminDashboard.sections.growthInvestmentsSection.input.growthInvestmentChannel"
              />

              <GenericDropDown
                data={categoryOptions}
                initialState={filter.categoryType === undefined ? "" : String(filter.categoryType)}
                handlerFuncion={(event) =>
                  onFilterChange(
                    new AdminGrowthInvestmentFilter({
                      ...filter,
                      categoryType:
                        event.target.value === ""
                          ? undefined
                          : (Number(event.target.value) as EAdminGrowthInvestmentCategoryType),
                    }),
                  )
                }
                descriptionKey="interactionDescriptions:pages.adminDashboard.sections.growthInvestmentsSection.select.select"
              />

              <GenericDropDown
                data={originOptions}
                initialState={filter.commercialOriginType === undefined ? "" : String(filter.commercialOriginType)}
                handlerFuncion={(event) =>
                  onFilterChange(
                    new AdminGrowthInvestmentFilter({
                      ...filter,
                      commercialOriginType:
                        event.target.value === "" ? undefined : (Number(event.target.value) as ECommercialOriginType),
                    }),
                  )
                }
                descriptionKey="interactionDescriptions:pages.adminDashboard.sections.growthInvestmentsSection.select.select2"
              />
            </div>
          </div>
        </div>
      </Animate>

      <div className="grid grid-cols-1 gap-3 p-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 min-[1800px]:grid-cols-5">
        <AdminMetricCard
          title={t("growth_investment_total")}
          value={convertToLocalMoney(dashboard.summary.totalInvestmentAmount)}
          icon={faCoins}
          tone="brand"
        />

        <AdminMetricCard
          title={t("growth_investment_return")}
          value={convertToLocalMoney(dashboard.summary.totalReturnAmount)}
          icon={faSackDollar}
          tone="positive"
        />

        <AdminMetricCard
          title={t("growth_investment_subscription_revenue")}
          value={convertToLocalMoney(dashboard.summary.subscriptionRevenueAmount)}
          icon={faFileInvoiceDollar}
          tone="info"
        />

        <AdminMetricCard
          title={t("growth_investment_manual_revenue")}
          value={convertToLocalMoney(dashboard.summary.associatedRevenueAmount)}
          icon={faSackDollar}
          tone="positive"
        />

        <AdminMetricCard
          title={t("growth_investment_result")}
          value={convertToLocalMoney(dashboard.summary.profitOrLossAmount)}
          icon={faArrowTrendUp}
          tone={dashboard.summary.profitOrLossAmount >= 0 ? "positive" : "negative"}
        />

        <AdminMetricCard
          title={t("growth_investment_roi")}
          value={`${dashboard.summary.roiPercentage.toFixed(2)}%`}
          icon={faChartLine}
          tone={dashboard.summary.roiPercentage >= 0 ? "positive" : "negative"}
        />

        <AdminMetricCard
          title={t("growth_investment_cpl")}
          value={convertToLocalMoney(dashboard.summary.costPerLeadAmount)}
          icon={faBullseye}
          tone="warning"
        />

        <AdminMetricCard
          title={t("growth_investment_cac")}
          value={convertToLocalMoney(dashboard.summary.costPerAcquiredCustomerAmount)}
          icon={faUserCheck}
          tone="warning"
        />

        <AdminMetricCard
          title={t("growth_investment_leads_total")}
          value={`${dashboard.summary.totalLeadsCount}`}
          icon={faBullseye}
          tone="info"
        />

        <AdminMetricCard
          title={t("growth_investment_new_subscriptions")}
          value={`${dashboard.summary.newSubscriptionsCount}`}
          icon={faFileInvoiceDollar}
          tone="violet"
        />
      </div>

      <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
        <div className="mt-4 grid grid-cols-1 gap-4 p-1 2xl:grid-cols-2 min-[2200px]:grid-cols-3">
          <SeriesChart title={t("growth_investment_series_investment")} data={investmentSeries} money />

          <SeriesChart title={t("growth_investment_series_return")} data={returnSeries} money />

          <SeriesChart title={t("growth_investment_series_result")} data={resultSeries} money />

          <SeriesChart title={t("growth_investment_top_channels")} data={topChannels} money />

          <SeriesChart title={t("growth_investment_top_campaigns")} data={topCampaigns} money />
        </div>
      </Animate>

      <div className="mt-3 p-2">
        <List
          messageCount={t("growth_investment_count")}
          messageEmpty={t("growth_investment_empty")}
          nameFindSearch={t("growth_investment_search")}
          nameDropdown={dropdownFilters}
          defaultStatusFilter={dropdownFilters[2]}
          dataChildren={
            <span className={`rounded-full bg-slate-100 px-3 py-1 text-xs font-bold ${resultClass}`}>
              {t("growth_investment_period_result")}: {convertToLocalMoney(dashboard.summary.profitOrLossAmount)}
            </span>
          }
          data={dashboard.investments.map((item, index) => {
            const associatedResult = item.associatedRevenueAmount - item.amount

            const hasReturn = item.associatedRevenueAmount > 0

            const resultLabel = hasReturn
              ? t("growth_investment_filter_return")
              : t("growth_investment_filter_without_return")

            const category = item.categoryType.toEAdminGrowthInvestmentCategoryTypeString()

            const origin = item.commercialOriginType.toECommercialOriginTypeString()

            return {
              id: item.id,
              index,
              title: item.title,
              linkImage: "",
              isDisable: !hasReturn,
              dataFilter: [
                item.title,
                category,
                origin,
                resultLabel,
                item.channelName ?? "",
                item.campaignName ?? "",
                item.responsibleName ?? "",
                item.targetAudience ?? "",
                item.notes ?? "",
                dateOnly(item.investmentDateUtc),
                convertToLocalMoney(item.amount),
                convertToLocalMoney(item.associatedRevenueAmount),
              ],
              children: (
                <AdminListCard
                  title={item.title}
                  subtitle={`${dateOnly(item.investmentDateUtc)} - ${category}`}
                  badge={resultLabel}
                  badgeClass={hasReturn ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}
                  cardClass={
                    hasReturn
                      ? "rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-3"
                      : "rounded-xl border-2 border-slate-200 bg-slate-50 p-3"
                  }
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <AdminListMetricItem
                      label={t("growth_investment_amount")}
                      value={convertToLocalMoney(item.amount)}
                      accent="text-red-700"
                    />

                    <AdminListMetricItem
                      label={t("growth_investment_associated_revenue")}
                      value={convertToLocalMoney(item.associatedRevenueAmount)}
                      accent="text-emerald-700"
                    />

                    <AdminListMetricItem
                      label={t("growth_investment_result")}
                      value={convertToLocalMoney(associatedResult)}
                      accent={associatedResult >= 0 ? "text-emerald-700" : "text-red-700"}
                    />

                    <AdminListMetricItem label={t("growth_investment_origin")} value={origin} />

                    <AdminListMetricItem label={t("growth_investment_leads")} value={item.leadsGenerated} />

                    <AdminListMetricItem label={t("growth_investment_customers")} value={item.acquiredCustomersCount} />
                  </div>

                  {(item.channelName || item.campaignName) && (
                    <div className="rounded-lg border border-orange-100 bg-orange-50 px-3 py-2 text-xs text-slate-700">
                      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                        {item.channelName && (
                          <p>
                            <strong>{t("growth_investment_channel")}:</strong> {item.channelName}
                          </p>
                        )}

                        {item.campaignName && (
                          <p>
                            <strong>{t("growth_investment_campaign")}:</strong> {item.campaignName}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </AdminListCard>
              ),
            }
          })}
          handleAddButton={() => setEditing(new AdminGrowthInvestment())}
          handleRefreshButton={onRefresh}
          handleOnClick={(id) => {
            const investment = dashboard.investments.find((item) => item.id === id)

            if (investment) setEditing(investment)
          }}
        />
      </div>

      {editing && (
        <AdminGrowthInvestmentPopup
          title={editing.id > 0 ? t("growth_investment_edit") : t("growth_investment_new")}
          investment={editing}
          onClose={() => setEditing(null)}
          onSuccess={onRefresh}
        />
      )}
    </div>
  )
}
